import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getMyProjects, getPlots, getProjectMapPdfUrl } from '../utils/api';
import { generateProjectPDF, generateAllProjectsPDF, generateWhatsAppMessage } from '../utils/pdf';
import { FileText, Share2, MapPin, LogOut, ShieldCheck, ListTree } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';

const STATUS_LABELS = {
  available: 'Available',
  booked: 'Booked',
  hold: 'On Hold',
  nfs: 'Not For Sale',
};

function Header({ admin, onLogout }) {
  const today = new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  return (
    <header style={{
      background: 'linear-gradient(135deg, #1D9E75 0%, #0F6E56 100%)',
      color: '#fff', padding: '0 28px', height: 68,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      position: 'sticky', top: 0, zIndex: 200,
      boxShadow: '0 2px 16px rgba(0,0,0,0.18)'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{
          width: 42, height: 42, background: 'rgba(255,255,255,0.18)',
          borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 800, fontSize: 13, border: '1.5px solid rgba(255,255,255,0.3)'
        }}>SRG</div>
        <div>
          <div style={{ fontSize: 17, fontWeight: 700 }}>SRG Real Estate Pvt. Ltd.</div>
          <div style={{ fontSize: 11.5, opacity: 0.82, marginTop: 1 }}>Associate Portal — Live Inventory</div>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ fontSize: 12, background: 'rgba(255,255,255,0.15)', padding: '5px 14px', borderRadius: 20 }}>
          {today}
        </div>
        <Link to="/team" style={{
          fontSize: 12, background: 'rgba(255,255,255,0.2)', padding: '6px 14px',
          borderRadius: 20, color: '#fff', textDecoration: 'none', fontWeight: 600,
          display: 'flex', alignItems: 'center', gap: 6, border: '1px solid rgba(255,255,255,0.3)'
        }}>
          <ListTree size={13} /> My Team
        </Link>
        <div style={{
          fontSize: 12, background: 'rgba(255,255,255,0.2)', padding: '6px 14px',
          borderRadius: 20, display: 'flex', alignItems: 'center', gap: 6,
          border: '1px solid rgba(255,255,255,0.3)'
        }}>
          <ShieldCheck size={13} /> {admin?.name}
        </div>
        <button onClick={onLogout} style={{
          fontSize: 12, background: 'rgba(255,255,255,0.2)', padding: '6px 14px',
          borderRadius: 20, color: '#fff', border: '1px solid rgba(255,255,255,0.3)',
          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'inherit', fontWeight: 600
        }}>
          <LogOut size={13} /> Logout
        </button>
      </div>
    </header>
  );
}

function MapModal({ project, onClose }) {
  if (!project) return null;
  const gMaps = `https://www.google.com/maps/search/?api=1&query=${project.map_query || `${project.name}+Jaipur+Rajasthan`}`;
  const appMaps = `https://maps.apple.com/?q=${encodeURIComponent(project.name + ' Jaipur Rajasthan')}`;

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-head">
          <h3>📍 {project.name}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <a href={gMaps} target="_blank" rel="noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: 14, borderRadius: 10, border: '1.5px solid var(--border)', cursor: 'pointer', marginBottom: 10 }}>
              <div style={{ width: 42, height: 42, background: '#E8F5E9', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>🗺️</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>Google Maps</div>
                <div style={{ fontSize: 12, color: 'var(--text3)' }}>Search on Google Maps</div>
              </div>
              <span style={{ color: 'var(--green)' }}>→</span>
            </div>
          </a>
          <a href={appMaps} target="_blank" rel="noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: 14, borderRadius: 10, border: '1.5px solid var(--border)', cursor: 'pointer' }}>
              <div style={{ width: 42, height: 42, background: '#E3F2FD', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>🍎</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>Apple Maps</div>
                <div style={{ fontSize: 12, color: 'var(--text3)' }}>Turn-by-turn directions</div>
              </div>
              <span style={{ color: 'var(--green)' }}>→</span>
            </div>
          </a>
          {project.has_map_pdf && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 14, padding: 14, marginTop: 10,
              borderRadius: 10, border: '1.5px solid var(--border)',
            }}>
              <div style={{ width: 42, height: 42, background: '#FFF3E0', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>📄</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>Site Layout PDF</div>
                <div style={{ fontSize: 12, color: 'var(--text3)' }}>{project.map_pdf_filename || 'Uploaded by SRG Real Estate'}</div>
              </div>
              <a href={getProjectMapPdfUrl(project.id)} target="_blank" rel="noreferrer"
                className="btn btn-outline btn-sm" style={{ textDecoration: 'none' }} title="View">
                View
              </a>
              <a href={getProjectMapPdfUrl(project.id, { download: true })}
                className="btn btn-green btn-sm" style={{ textDecoration: 'none' }} title="Download">
                Download
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AssociateDashboard() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [activeProjectIdx, setActiveProjectIdx] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const [mapProject, setMapProject] = useState(null);

  const { data: projects = [], isLoading } = useQuery({ queryKey: ['my-projects'], queryFn: getMyProjects });

  const activeProject = projects[activeProjectIdx];

  const { data: plots = [] } = useQuery({
    queryKey: ['plots', activeProject?.id],
    queryFn: () => getPlots({ project_id: activeProject.id }),
    enabled: !!activeProject?.id,
  });

  const filteredPlots = statusFilter === 'all' ? plots : plots.filter(p => p.status === statusFilter);

  const handleLogout = () => { logout(); navigate('/admin/login'); };

  const handleWAShare = () => {
    if (!activeProject) return;
    const msg = generateWhatsAppMessage(activeProject, plots);
    window.open('https://api.whatsapp.com/send?text=' + encodeURIComponent(msg), '_blank');
  };

  if (isLoading) return (
    <>
      <Header admin={admin} onLogout={handleLogout} />
      <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}><div className="spinner" /></div>
    </>
  );

  if (!projects.length) return (
    <>
      <Header admin={admin} onLogout={handleLogout} />
      <div className="empty-state" style={{ padding: 80 }}>
        <p>No projects have been assigned to you yet.</p>
        <p style={{ fontSize: 12 }}>Please contact your admin to get project access.</p>
      </div>
    </>
  );

  return (
    <>
      <Header admin={admin} onLogout={handleLogout} />
      <div style={{ maxWidth: 1160, margin: '0 auto', padding: '24px 20px 40px' }}>

        {/* Action Bar */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 22 }}>
          <button className="btn btn-green" onClick={() => generateProjectPDF(activeProject, plots)} disabled={!activeProject}>
            <FileText size={15} /> PDF Generate Karo
          </button>
          <button className="btn btn-wa" onClick={handleWAShare} disabled={!activeProject}>
            <Share2 size={15} /> WhatsApp Share
          </button>
          <button className="btn btn-blue" onClick={() => generateAllProjectsPDF(projects)} disabled={!projects.length}>
            <FileText size={15} /> My Projects PDF
          </button>
          <button className="btn btn-outline" onClick={() => setMapProject(activeProject)} disabled={!activeProject}>
            <MapPin size={15} /> Project Map
          </button>
        </div>

        {/* Project Tabs */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 18, overflowX: 'auto' }}>
          {projects.map((proj, i) => (
            <button
              key={proj.id}
              onClick={() => { setActiveProjectIdx(i); setStatusFilter('all'); }}
              style={{
                padding: '8px 18px', borderRadius: 20, border: '1.5px solid',
                fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500,
                transition: 'all 0.15s', whiteSpace: 'nowrap',
                background: i === activeProjectIdx ? 'var(--green)' : 'var(--white)',
                color: i === activeProjectIdx ? '#fff' : 'var(--text2)',
                borderColor: i === activeProjectIdx ? 'var(--green)' : 'var(--border)',
                boxShadow: i === activeProjectIdx ? '0 2px 8px rgba(29,158,117,0.3)' : 'none',
              }}
            >
              {proj.name}
            </button>
          ))}
        </div>

        {/* Project Panel */}
        {activeProject && (
          <div className="card" style={{ overflow: 'hidden' }}>
            <div className="card-header" style={{ background: '#f8fffe' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span className="card-title">{activeProject.name}</span>
                <span style={{ fontSize: 12, background: 'var(--green-light)', color: 'var(--green-dark)', padding: '3px 10px', borderRadius: 20, fontWeight: 600 }}>
                  {activeProject.type}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {[
                  { label: `${activeProject.available} Available`, cls: 'badge-available' },
                  { label: `${activeProject.booked} Booked`, cls: 'badge-booked' },
                  { label: `${activeProject.nfs} NFS`, cls: 'badge-nfs' },
                  { label: `${activeProject.total} Total`, cls: 'badge-hold' },
                ].map(b => (
                  <span key={b.label} className={`badge ${b.cls}`}>{b.label}</span>
                ))}
              </div>
            </div>

            <div style={{ padding: '12px 20px', background: 'var(--white)', borderBottom: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>
                <span>Booking Progress</span>
                <span>{activeProject.total > 0 ? Math.round((activeProject.booked / activeProject.total) * 100) : 0}% Booked</span>
              </div>
              <div className="progress-track">
                <div className="progress-fill" style={{ width: `${activeProject.total > 0 ? (activeProject.booked / activeProject.total) * 100 : 0}%` }} />
              </div>
            </div>

            <div className="filter-bar">
              <span style={{ fontSize: 12, color: 'var(--text3)', fontWeight: 500 }}>Filter:</span>
              {['all', 'available', 'booked', 'hold', 'nfs'].map(s => (
                <button key={s} className={`fchip ${statusFilter === s ? 'active' : ''}`} onClick={() => setStatusFilter(s)}>
                  {s === 'all' ? `All (${plots.length})` : `${STATUS_LABELS[s]} (${plots.filter(p => p.status === s).length})`}
                </button>
              ))}
            </div>

            <div className="tbl-wrap">
              <table>
                <thead>
                  <tr>
                    <th>#</th><th>Plot No.</th><th>Facing</th><th>Size</th><th>Status</th><th>Customer</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPlots.length === 0 ? (
                    <tr><td colSpan={6} style={{ textAlign: 'center', padding: '2.5rem', color: 'var(--text3)' }}>
                      No plots match this filter
                    </td></tr>
                  ) : filteredPlots.map((plot, i) => (
                    <tr key={plot.id}>
                      <td className="mono">{i + 1}</td>
                      <td style={{ fontWeight: 700, fontSize: 13.5 }}>{plot.plot_no}</td>
                      <td>{plot.facing || '—'}</td>
                      <td className="mono">{plot.size || '—'}</td>
                      <td><span className={`badge badge-${plot.status}`}>{STATUS_LABELS[plot.status] || plot.status}</span></td>
                      <td style={{ color: 'var(--text2)' }}>{plot.booked_by || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <footer style={{ textAlign: 'center', padding: 24, color: 'var(--text3)', fontSize: 12, borderTop: '1px solid var(--border)', background: 'var(--white)', marginTop: 10 }}>
        <strong style={{ color: 'var(--green)' }}>SRG Real Estate Pvt. Ltd.</strong> &nbsp;|&nbsp;
        Read-only Associate View &mdash; data updates live from admin panel
      </footer>

      {mapProject && <MapModal project={mapProject} onClose={() => setMapProject(null)} />}
    </>
  );
}
